import React, { useState, useEffect } from 'react';

import { Typography, makeStyles, Box, Paper, Button, CircularProgress, Fade } from '@material-ui/core';
import { Business } from '@material-ui/icons';
import { useSnackbar } from 'notistack';

import AddDepartmentModal from './AddDepartmentModal';
import AddFacilityModal from './AddFacilityModal';
import FacilityListItem from './FacilityListItem';
import FacilityService from '../../services/facility.service';
import ConfirmationModal from '../common/ConfirmationModal';

const useStyles = makeStyles((theme) => ({
  accordion: {
    '&.Mui-expanded': {
      margin: `${theme.spacing(2)}px 0`,
    },
    '&.MuiAccordion-root:before': {
      display: 'none',
    },
    borderRadius: 8,
    boxShadow: '0px 0px 10px 0px #0000001A',
    marginBottom: theme.spacing(2),
  },
  emptyState: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(6),
    textAlign: 'center',
  },
  emptyStateIcon: {
    color: theme.palette.text.disabled,
    fontSize: 48,
    marginBottom: theme.spacing(2),
  },
  facilityName: {
    fontSize: 14,
    fontWeight: 'bold',
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  root: {
    width: '100%',
  },
  secondTitle: {
    color: '#172B4D',
    fontSize: '18px',
    fontWeight: 'bold',
  },
}));

const Facilities = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const facilityService = new FacilityService();

  const [facilities, setFacilities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFacilityModalOpen, setIsFacilityModalOpen] = useState(false);
  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [hoveredDepartment, setHoveredDepartment] = useState(null);
  const [editFacilityId, setEditFacilityId] = useState(null);
  const [editDepartmentId, setEditDepartmentId] = useState(null);
  const [confirmDeleteFacilityId, setConfirmDeleteFacilityId] = useState(null);
  const [confirmDeleteDepartmentId, setConfirmDeleteDepartmentId] = useState(null);
  const [confirmDeleteFacilityForDepartmentId, setConfirmDeleteFacilityForDepartmentId] = useState(null);
  const [isDeletingFacility, setIsDeletingFacility] = useState(false);
  const [isDeletingDepartment, setIsDeletingDepartment] = useState(false);

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    setIsLoading(true);
    try {
      const data = await facilityService.getAllFacilities();
      setFacilities(data);
    } catch (error) {
      enqueueSnackbar('Failed to load facilities', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleOpenFacilityModal = () => {
    setIsFacilityModalOpen(true);
  };

  const handleCloseFacilityModal = () => {
    setIsFacilityModalOpen(false);
  };

  const handleOpenDepartmentModal = (facilityId) => {
    setSelectedFacilityId(facilityId);
    setIsDepartmentModalOpen(true);
  };

  const handleCloseDepartmentModal = () => {
    setIsDepartmentModalOpen(false);
    setSelectedFacilityId(null);
  };

  const handleAddFacility = (newFacility) => {
    setFacilities([...facilities, newFacility]);
    handleCloseFacilityModal();
  };

  const handleUpdateFacility = (facilityId, updatedFacility) => {
    setFacilities(facilities.map((facility) => (facility.id === facilityId ? updatedFacility : facility)));
    setEditFacilityId(null);
  };

  const handleDeleteFacility = async (facilityId) => {
    if (isDeletingFacility) return;

    try {
      setIsDeletingFacility(true);
      await facilityService.deleteFacility(facilityId);
      setFacilities(facilities.filter((facility) => facility.id !== facilityId));
      setConfirmDeleteFacilityId(null);
      enqueueSnackbar('Facility deleted successfully', { variant: 'success' });
    } catch (error) {
      const errorMessage = error.message || error.error || 'Failed to delete facility';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setConfirmDeleteFacilityId(null);
    } finally {
      setIsDeletingFacility(false);
    }
  };

  const handleAddDepartment = (newDepartment) => {
    setFacilities(
      facilities.map((facility) => {
        if (facility.id === selectedFacilityId) {
          return {
            ...facility,
            departments: [...facility.departments, newDepartment],
          };
        }
        return facility;
      })
    );

    handleCloseDepartmentModal();
  };

  const handleUpdateDepartment = (departmentId, facilityId, updatedDepartment) => {
    setFacilities(
      facilities.map((facility) => {
        if (facility.id === facilityId) {
          return {
            ...facility,
            departments: facility.departments.map((dept) => (dept.id === departmentId ? updatedDepartment : dept)),
          };
        }
        return facility;
      })
    );

    setEditDepartmentId(null);
    setSelectedFacilityId(null);
  };

  const handleDeleteDepartment = async (departmentId, facilityId) => {
    if (isDeletingDepartment) return;

    try {
      setIsDeletingDepartment(true);
      await facilityService.deleteDepartment(departmentId, facilityId);

      setFacilities(
        facilities.map((facility) => {
          if (facility.id === facilityId) {
            return {
              ...facility,
              departments: facility.departments.filter((dept) => dept.id !== departmentId),
            };
          }
          return facility;
        })
      );

      setConfirmDeleteDepartmentId(null);
      setConfirmDeleteFacilityForDepartmentId(null);
      enqueueSnackbar('Department deleted successfully', { variant: 'success' });
    } catch (error) {
      const errorMessage = error.message || error.error || 'Failed to delete department';

      enqueueSnackbar(errorMessage, { variant: 'error' });
      setConfirmDeleteDepartmentId(null);
      setConfirmDeleteFacilityForDepartmentId(null);
    } finally {
      setIsDeletingDepartment(false);
    }
  };

  const handleMouseEnter = (departmentId) => {
    setHoveredDepartment(departmentId);
  };

  const handleMouseLeave = () => {
    setHoveredDepartment(null);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.secondTitle}>Facilities</Typography>

        <Button color="primary" variant="contained" onClick={handleOpenFacilityModal}>
          + Add Facility
        </Button>
      </div>

      {isLoading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && facilities?.length === 0 ? (
        <Paper className={classes.emptyState}>
          <Business className={classes.emptyStateIcon} />
          <Typography variant="h6">No facilities found</Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Get started by adding your first medical facility
          </Typography>

          <Button variant="contained" onClick={handleOpenFacilityModal}>
            Add Facility
          </Button>
        </Paper>
      ) : (
        <Fade in={!isLoading}>
          <div>
            {facilities.map((facility) => (
              <FacilityListItem
                key={facility.id}
                facility={facility}
                expanded={expanded}
                onAccordionChange={handleAccordionChange}
                onEditFacility={setEditFacilityId}
                onDeleteFacility={setConfirmDeleteFacilityId}
                onAddDepartment={handleOpenDepartmentModal}
                onEditDepartment={(departmentId, facilityId) => {
                  setEditDepartmentId(departmentId);
                  setSelectedFacilityId(facilityId);
                }}
                onDeleteDepartment={(departmentId, facilityId) => {
                  setConfirmDeleteDepartmentId(departmentId);
                  setConfirmDeleteFacilityForDepartmentId(facilityId);
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
            ))}
          </div>
        </Fade>
      )}

      <AddFacilityModal open={isFacilityModalOpen} onClose={handleCloseFacilityModal} onAdd={handleAddFacility} />

      <AddDepartmentModal
        open={isDepartmentModalOpen}
        onClose={handleCloseDepartmentModal}
        onAdd={handleAddDepartment}
        facilityName={selectedFacilityId ? facilities.find((f) => f.id === selectedFacilityId)?.name : ''}
        facilityId={selectedFacilityId}
      />

      {/* Edit Facility Modal */}
      {editFacilityId && (
        <AddFacilityModal
          open={!!editFacilityId}
          onClose={() => setEditFacilityId(null)}
          onAdd={(updatedFacility) => {
            handleUpdateFacility(editFacilityId, updatedFacility);
          }}
          initialData={facilities.find((f) => f.id === editFacilityId)}
          isEditing
        />
      )}

      {/* Edit Department Modal */}
      {editDepartmentId && selectedFacilityId && (
        <AddDepartmentModal
          open={!!editDepartmentId}
          onClose={() => {
            setEditDepartmentId(null);
            setSelectedFacilityId(null);
          }}
          onAdd={(updatedDepartment) => {
            handleUpdateDepartment(editDepartmentId, selectedFacilityId, updatedDepartment);
          }}
          facilityName={facilities.find((f) => f.id === selectedFacilityId)?.name}
          facilityId={selectedFacilityId}
          initialData={facilities.find((f) => f.id === selectedFacilityId)?.departments.find((d) => d.id === editDepartmentId)}
          isEditing
        />
      )}

      <ConfirmationModal
        open={!!confirmDeleteFacilityId}
        title="Delete Facility"
        message="Are you sure you want to delete this facility? This will also delete all departments associated with it."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={() => handleDeleteFacility(confirmDeleteFacilityId)}
        onCancel={() => setConfirmDeleteFacilityId(null)}
        loading={isDeletingFacility}
      />

      <ConfirmationModal
        open={!!confirmDeleteDepartmentId && !!confirmDeleteFacilityForDepartmentId}
        title="Delete Department"
        message="Are you sure you want to delete this department?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={() => handleDeleteDepartment(confirmDeleteDepartmentId, confirmDeleteFacilityForDepartmentId)}
        onCancel={() => {
          setConfirmDeleteDepartmentId(null);
          setConfirmDeleteFacilityForDepartmentId(null);
        }}
        loading={isDeletingDepartment}
      />
    </div>
  );
};

export default Facilities;
